import {useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {
  PersonaIdentityFields,
  PersonaLicenciaDialog,
  clearChangedIdentityErrors,
  identityPayload,
  validateIdentity,
  type PersonaIdentity,
} from '@/components/roster'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {SectionCard} from '@/components/section-card'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {HelpIcon} from '@/components/help-icon'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {
  mintPartnerLicenciaUploadTarget,
  usePartnerLicenciaMutations,
  usePartnerMembers,
  useCreatePartnerMember,
  useRemovePartnerMember,
  type Partner,
  type PartnerMember,
} from '@/hooks/inci/aliados/use-partners'
import {translateApiError} from '@/lib/errors'

function licenciaTone(estado: PartnerMember['licencia']['estado']): 'green' | 'red' | 'amber' | 'neutral' {
  if (estado === 'verificada') return 'green'
  if (estado === 'rechazada') return 'red'
  if (estado === 'por_verificar') return 'amber'
  return 'neutral'
}

function LicenciaBadge({licencia}: {licencia: PartnerMember['licencia']}) {
  const {t} = useTranslation()
  const key = licencia.estado ?? 'none'
  return <Tag tone={licenciaTone(licencia.estado)}>{t(`partners.licenciaEstado.${key}`)}</Tag>
}

const EMPTY_IDENTITY: PersonaIdentity = {
  name: '',
  last_name: '',
  cedula: '',
  tipo_documento: 'CC',
  email: '',
  phone: '',
}

function AddMemberDialog({partnerId, open, onClose}: {partnerId: string; open: boolean; onClose: () => void}) {
  const {t} = useTranslation()
  const create = useCreatePartnerMember(partnerId)
  const [identity, setIdentity] = useState<PersonaIdentity>(EMPTY_IDENTITY)
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof PersonaIdentity, string>>>({})
  const [error, setError] = useState<string | null>(null)
  // El popover de ayuda se porta al `DialogContent`, no al body: ver `ui/popover.tsx`.
  const [dialogo, setDialogo] = useState<HTMLElement | null>(null)

  function reset() {
    setIdentity(EMPTY_IDENTITY)
    setFieldErrors({})
    setError(null)
    create.reset()
  }

  function handleOpenChange(next: boolean) {
    if (!next) {
      reset()
      onClose()
    }
  }

  function onIdentityChange(next: PersonaIdentity) {
    setIdentity(next)
    setFieldErrors((prev) => clearChangedIdentityErrors(prev, next, identity))
  }

  function blurField(field: keyof PersonaIdentity) {
    setFieldErrors((prev) => ({...prev, [field]: validateIdentity(identity)[field]}))
  }

  // Named `validate*`: the R4 gate only recognizes a direct `@/lib/validation` import or a
  // local `validate*` function, not a validator re-exported through `roster.ts`.
  function validateAndSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const errors = validateIdentity(identity)
    setFieldErrors(errors)
    if (Object.values(errors).some(Boolean)) return
    create.mutate(identityPayload(identity), {
      onSuccess: () => handleOpenChange(false),
      onError: (err) => setError(translateApiError(err)),
    })
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent ref={setDialogo} data-testid="partner-member-create-dialog">
        <DialogHeader>
          <div className="flex items-center gap-1.5">
            <DialogTitle>{t('partners.members.create.title')}</DialogTitle>
            <HelpIcon
              label={t('partners.members.create.tipLabel')}
              testId="partner-member-create-tip"
              contenedor={dialogo}>
              {t('partners.members.create.description')}
            </HelpIcon>
          </div>
        </DialogHeader>
        <form onSubmit={validateAndSubmit} noValidate className="space-y-4">
          <div className="flex flex-col gap-4">
            <PersonaIdentityFields
              value={identity}
              onChange={onIdentityChange}
              tKey="partners.members.create"
              testPrefix="partner-member"
              errors={fieldErrors}
              onBlurField={blurField}
            />
          </div>
          <FormDialogFooter
            error={error}
            pending={create.isPending}
            submitLabel={create.isPending ? t('partners.members.create.creating') : t('partners.members.create.submit')}
            onCancel={() => handleOpenChange(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}

function MemberRow({
  member,
  onLicencia,
  onRemove,
}: {
  member: PartnerMember
  onLicencia: (m: PartnerMember) => void
  onRemove: (m: PartnerMember) => void
}) {
  const {t} = useTranslation()
  return (
    <li
      className="flex items-center justify-between gap-2 border-b border-border py-2.5 last:border-0"
      data-testid={`partner-member-row-${member.id}`}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="truncate text-sm font-medium text-foreground">
            {member.name} {member.last_name}
          </span>
          {member.is_founding && <Tag tone="ink">{t('partners.members.founding')}</Tag>}
          {!member.active && <Tag tone="neutral">{t('partners.estado.inactive')}</Tag>}
        </div>
        <span className="block truncate font-mono text-xs text-muted-foreground">{member.cedula}</span>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <LicenciaBadge licencia={member.licencia} />
        <Button
          size="sm"
          variant="ghost"
          data-testid={`partner-member-licencia-${member.id}`}
          onClick={() => onLicencia(member)}>
          {t('partners.actions.licencia')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="text-destructive hover:text-destructive disabled:text-muted-foreground"
          data-testid={`partner-member-remove-${member.id}`}
          disabled={member.is_founding}
          title={member.is_founding ? t('partners.members.foundingCannotRemove') : undefined}
          onClick={() => onRemove(member)}>
          {t('partners.members.remove')}
        </Button>
      </div>
    </li>
  )
}

export function MiembrosSection({partner}: {partner: Partner}) {
  const {t} = useTranslation()
  const {data, isLoading} = usePartnerMembers(partner.partner_id, {page: 1, search: ''})
  const removeMember = useRemovePartnerMember(partner.partner_id)
  const {save, verify} = usePartnerLicenciaMutations()
  const [adding, setAdding] = useState(false)
  const [licenciaTarget, setLicenciaTarget] = useState<PartnerMember | null>(null)
  const [removeTarget, setRemoveTarget] = useState<PartnerMember | null>(null)

  function confirmRemove() {
    if (!removeTarget) return
    removeMember.mutate(removeTarget.id, {onSuccess: () => setRemoveTarget(null)})
  }

  return (
    <SectionCard
      title={t('partners.members.title')}
      actions={
        <Button size="sm" variant="outline" data-testid="partner-member-new" onClick={() => setAdding(true)}>
          {t('partners.members.new')}
        </Button>
      }
      bodyClassName="space-y-3 px-5 py-4"
      testId="partner-detail-miembros">
      {isLoading && !data ? (
        <Skeleton className="h-24 w-full" />
      ) : data && data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('partners.members.empty')}</p>
      ) : (
        <ul>
          {(data?.items ?? []).map((member) => (
            <MemberRow key={member.id} member={member} onLicencia={setLicenciaTarget} onRemove={setRemoveTarget} />
          ))}
        </ul>
      )}

      <AddMemberDialog partnerId={partner.partner_id} open={adding} onClose={() => setAdding(false)} />

      <PersonaLicenciaDialog
        target={
          licenciaTarget
            ? {
                userId: licenciaTarget.user_id,
                name: `${licenciaTarget.name} ${licenciaTarget.last_name}`,
                licencia: licenciaTarget.licencia,
              }
            : null
        }
        tKey="partners.licenciaDialog"
        testPrefix="partner-member"
        onClose={() => setLicenciaTarget(null)}
        mintUploadTarget={mintPartnerLicenciaUploadTarget}
        onSave={(input) => save.mutateAsync(input)}
        onVerify={(userId) => verify.mutateAsync(userId).then(() => setLicenciaTarget(null))}
        saving={save.isPending}
        verifying={verify.isPending}
      />

      <ConfirmDialog
        open={!!removeTarget}
        onOpenChange={(open) => !open && setRemoveTarget(null)}
        title={t('partners.members.removeDialog.title')}
        description={t('partners.members.removeDialog.description', {
          name: removeTarget ? `${removeTarget.name} ${removeTarget.last_name}` : '',
        })}
        confirmLabel={t('partners.members.remove')}
        cancelLabel={t('common.cancel')}
        pending={removeMember.isPending}
        onConfirm={confirmRemove}
      />
    </SectionCard>
  )
}
